import { createApp } from "vue";
import { createPinia } from "pinia";

import { readCookie } from "common/utils/cookie";
import { getQueryParams, removeQueryParam } from "common/utils/browser";
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";
import { getFileFromUrl } from "common/utils/file";
import { parseArtworkUrls } from "common/utils/json";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import store from "./store/store";
import i18n from "../vue-cart/vue-i18n";
import defaultSurfaces from "./misc/default-surfaces";
import ProductDetailsPrint from "./ProductDetailsPrint.vue";
import { SURFACES, SIDES, MODES } from "./misc/constants";

const $getColorPickerLinks = () => document.querySelectorAll(".js-color-variant-selector");

const initColorPickerLinks = () => {
  const isEdit = Boolean(window.jiffy_made_config?.is_edit);
  const { asgid } = getQueryParams();

  if (!isEdit || !asgid) return;

  const $colorPickerLinks = $getColorPickerLinks();

  $colorPickerLinks.forEach($link => {
    // eslint-disable-next-line no-param-reassign
    $link.href = `${$link.href}&asgid=${asgid}&edit=true`;
  });
};

const initEditMode = async () => {
  const isEdit = Boolean(window.jiffy_made_config?.is_edit);
  if (!isEdit) return;

  const { asgid } = getQueryParams();
  if (!asgid) return;

  setTimeout(() => {
    store.dispatch("app/restore", { asgid });
  }, 1000);
};

const initAutoUpload = async () => {
  const { artwork_urls: imageUrls, file_content_type: imageType, side } = getQueryParams();
  if (!imageUrls || !imageType) return;

  // Determine which side to attach artwork to
  // Default to "front" if no side parameter is provided
  const targetSide = side === "back" ? "back" : "front";

  removeQueryParam("artwork_urls");
  removeQueryParam("file_content_type");
  removeQueryParam("side");

  const parsedUrls = parseArtworkUrls(imageUrls);
  if (!parsedUrls) return;

  const { imageUrl, lastArtworkId: existingArtworkId } = parsedUrls;

  try {
    const [file] = await Promise.all([
      getFileFromUrl(imageUrl, `artwork_image-${Date.now()}`, imageType),
      store.dispatch(`artwork_${targetSide}/removeArtwork`)
    ]);

    store.commit("app/setMode", MODES.MADE);
    store.dispatch(`artwork_${targetSide}/upload`, { file, existingArtworkId });
    ScrollToElement.scrollIntoViewBySelector("#vue-product-details-print");
  } catch (error) {
    console.error("Error during auto upload:", error);
  }
};

const initDesignLibraryModal = () => {
  const { open_design_library: openDesignLibrary } = getQueryParams();
  if (!openDesignLibrary) return;

  const userType = readCookie("name") ? "signed_in" : "guest_user";
  if (userType !== "signed_in") return;

  const dtfStore = useDtfStore();
  if (dtfStore) {
    store.commit("app/setMode", "made");
    store.commit("setArtworkHistoryModalIndex", 0);
    dtfStore.setShowArtworkHistoryModal(true);
  }
};

export const initDefaultProps = () => {
  const userType = readCookie("name") ? "signed_in" : "guest_user";
  store.commit("setUserType", userType);
  store.commit("product/setDefaultData", window.jiffy_made_product_props);
  store.dispatch("creation/fetchAreas");
  store.dispatch("product/fetchProduct").then(initEditMode);
  store.commit("setConfig", window.jiffy_made_config);

  store.commit("surface_front/setDefaultSurface", defaultSurfaces.front);
  store.commit("surface_back/setDefaultSurface", defaultSurfaces.back);
  store.commit("surface_front/setSurfaceLabel", SURFACES.FRONT);
  store.commit("surface_back/setSurfaceLabel", SURFACES.BACK);
  store.commit("artwork_front/setSide", SIDES.FRONT);
  store.commit("artwork_back/setSide", SIDES.BACK);

  if (document.querySelector(".js-ab-made-monetization-enabled")) {
    const dtfStore = useDtfStore();
    dtfStore.setIsJiffyMade(true);
    dtfStore.setCatalogNumber("DTF001");

    dtfStore.setBusinessLine("Print");
    dtfStore.setCachedArtworkPrices(window.jiffy_made_config.artwork_prices);
    dtfStore.setEnableRedraw(window.jiffy_made_config.redraw_note_jiffymade);
    dtfStore.setForcePdpTransfersRedraw(window.jiffy_made_config.redraw_note_jiffymade);
    dtfStore.initDefaultValues({ skipDeliveryDateLoading: true });
    dtfStore.setProductId(432292);
    dtfStore.$state.artworksState = [
      ...dtfStore.$state.artworksState,
      { ...dtfStore.simulation, id: 0, index: 0 },
      { ...dtfStore.simulation, id: 1, index: 1 }
    ];
    dtfStore.setExperiments({
      ...store.getters.experiments,
      ab_ai_monetization_v4: 1 // Hardcoded. Need forcing to enable new UI for tabs content
    });
    store.commit("artwork_front/setMode", "front");
    store.commit("artwork_back/setMode", "back");
  } else {
    store.dispatch("artwork_front/createDtfChannel");
    store.dispatch("artwork_back/createDtfChannel");
  }
};

const subscribeToDtfStore = () => {
  const dtfStore = useDtfStore();
  dtfStore.$subscribe((mutation, _) => {
    if (mutation?.events?.key !== "artworksState") return;

    store.commit("triggerSubscribers");
  });
};

const initPageData = () => {
  const activeColor = document.getElementById("active-color-name")?.value;
  const activeCompositionId = document.querySelector(
    `.js-color-variant-selector[data-color="${activeColor}"]`
  )?.dataset?.compositionId;

  if (activeColor && activeCompositionId) {
    store.dispatch("app/setActiveComposition", {
      compositionId: activeCompositionId,
      color: activeColor
    });
  }
};

const initProductDetailsPrint = () => {
  const $wrapper = document.getElementById("vue-product-details-print");

  if (!$wrapper) return;

  const ProductDetailsPrintWrapper = createApp({});

  ProductDetailsPrintWrapper.use(createPinia());
  ProductDetailsPrintWrapper.component("ProductDetailsPrint", ProductDetailsPrint);
  ProductDetailsPrintWrapper.use(store);
  ProductDetailsPrintWrapper.use(i18n);

  initDefaultProps();
  initPageData();
  initAutoUpload();
  initColorPickerLinks();
  subscribeToDtfStore();

  ProductDetailsPrintWrapper.mount("#vue-product-details-print");

  initDesignLibraryModal();
};

export default initProductDetailsPrint;
